import React, { useState } from "react";

import { CheckCircleFilled, InfoCircleOutlined, RightOutlined } from "@ant-design/icons";
import { Button } from "antd";
import Modal from "antd/es/modal/Modal";
import moment from "moment";

import StyledScrollbar from "../../../../Common/UIComponents/StyledScrollbar";
import { useAccessStatus } from "../../../../grace-period/hooks/useAccessStatus";

import { isPrepaidOrSubscriptionCustomer } from "../../../../MyAccount/UsageMetrics/utils";

import { ReactComponent as RefundIcon } from "../../../../../assets/images/product/refund-2-fill.svg";

import "./SendNewSampleWizardModal.css";

const DATE_OPTIONS_COUNT = 6;
const STEP_TIMELINE = 1;
const STEP_REVIEW = 2;

function generateDateOptions() {
  return Array.from({ length: DATE_OPTIONS_COUNT }, (_, i) => moment().add(i, "days"));
}

export default function SendNewSampleWizardModal({
  testNames = [],
  sampleSize = "",
  loading = false,
  onCancel,
  onSubmit,
}) {
  const dates = generateDateOptions();
  const [step, setStep] = useState(STEP_TIMELINE);
  const [selectedDate, setSelectedDate] = useState(() => moment());
  const { customer_type } = useAccessStatus();
  const showNoCreditsCharged = step === STEP_REVIEW && isPrepaidOrSubscriptionCustomer(customer_type);
  const tests = Array.from(new Set(testNames));

  const handleContinue = () => {
    if (step === STEP_TIMELINE && selectedDate) {
      setStep(STEP_REVIEW);
    }
  };

  const handleSubmit = () => {
    if (!selectedDate || loading) return;
    onSubmit(selectedDate);
  };

  return (
    <Modal
      open
      maskClosable={false}
      closable={false}
      centered
      footer={null}
      wrapClassName="SendNewSampleWizardModal"
      className="SendNewSampleWizardModal"
      width="520px"
    >
      <div className="SendNewSampleWizardModal__Header">
        <span className="SendNewSampleWizardModal__Title">Send new sample</span>
        <span className="SendNewSampleWizardModal__Subtitle">
          Send a new sample to verify the result
        </span>
      </div>

      <div className="SendNewSampleWizardModal__Stepper">
        <div
          className={[
            "SendNewSampleWizardModal__Step",
            step === STEP_TIMELINE ? "SendNewSampleWizardModal__Step--active" : "",
            step > STEP_TIMELINE ? "SendNewSampleWizardModal__Step--done" : "",
          ].filter(Boolean).join(" ")}
        >
          <div className="SendNewSampleWizardModal__StepContent">
            {step > STEP_TIMELINE ? (
              <span className="SendNewSampleWizardModal__StepCheck" aria-hidden>
                <CheckCircleFilled />
              </span>
            ) : (
              <span className="SendNewSampleWizardModal__StepBadge">1</span>
            )}
            <span className="SendNewSampleWizardModal__StepLabel">Sample Timeline</span>
          </div>
          {step === STEP_TIMELINE && <div className="SendNewSampleWizardModal__StepUnderline" />}
        </div>
        <RightOutlined className="SendNewSampleWizardModal__StepChevron" />
        <div
          className={[
            "SendNewSampleWizardModal__Step",
            step === STEP_REVIEW ? "SendNewSampleWizardModal__Step--active" : "",
          ].filter(Boolean).join(" ")}
        >
          <div className="SendNewSampleWizardModal__StepContent">
            <span className="SendNewSampleWizardModal__StepBadge">2</span>
            <span className="SendNewSampleWizardModal__StepLabel">Review &amp; Submit</span>
          </div>
          {step === STEP_REVIEW && <div className="SendNewSampleWizardModal__StepUnderline" />}
        </div>
      </div>

      <div className="SendNewSampleWizardModal__Body">
        {step === STEP_TIMELINE ? (
          <>
            <div className="SendNewSampleWizardModal__Alert SendNewSampleWizardModal__Alert--warning">
              <InfoCircleOutlined className="SendNewSampleWizardModal__AlertIcon SendNewSampleWizardModal__AlertIcon--warning" />
              <div className="SendNewSampleWizardModal__AlertText">
                <span className="SendNewSampleWizardModal__AlertTitle SendNewSampleWizardModal__AlertTitle--warning">
                  New Sample Required
                </span>
                <span className="SendNewSampleWizardModal__AlertBody">
                  A new sample is required to verify this result. Please submit a new sample for retesting.
                </span>
              </div>
            </div>

            <div className="SendNewSampleWizardModal__Section">
              <span className="SendNewSampleWizardModal__SectionTitle">Step 1 · Sample Timeline</span>
              <span className="SendNewSampleWizardModal__SectionHint">
                Choose when you will send the new sample.
              </span>
              <div className="SendNewSampleWizardModal__DateRow">
                {dates.map((date, i) => {
                  const isSelected = selectedDate && date.isSame(selectedDate, "day");
                  const isToday = i === 0;
                  return (
                    <div key={date.format("YYYY-MM-DD")} className="SendNewSampleWizardModal__DatePillWrapper">
                      <Button
                        type="text"
                        className={[
                          "SendNewSampleWizardModal__DatePill",
                          isSelected ? "SendNewSampleWizardModal__DatePill--selected" : "",
                        ].filter(Boolean).join(" ")}
                        onClick={() => setSelectedDate(date)}
                      >
                        <span className="SendNewSampleWizardModal__DatePillText">
                          {date.format("MMM")}
                          <br />
                          {date.format("D")}
                        </span>
                      </Button>
                      {isToday && (
                        <span className="SendNewSampleWizardModal__TodayLabel">Today</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="SendNewSampleWizardModal__Alert SendNewSampleWizardModal__Alert--info">
              <InfoCircleOutlined className="SendNewSampleWizardModal__AlertIcon" />
              <div className="SendNewSampleWizardModal__AlertText">
                <span className="SendNewSampleWizardModal__AlertTitle">
                  {`${sampleSize ? `${sampleSize} ` : ""}Sample Required`}
                </span>
                <span className="SendNewSampleWizardModal__AlertBody">
                  {`To verify the result, please provide ${sampleSize ? `${sampleSize} of ` : ""}a fresh sample. This sample will be used for further testing.`}
                </span>
              </div>
            </div>

            <div className="SendNewSampleWizardModal__Section">
              <span className="SendNewSampleWizardModal__SectionTitle">Step 2 · Review &amp; Submit</span>
              <span className="SendNewSampleWizardModal__SectionHint">
                Review your request before submitting
              </span>
              <span className="SendNewSampleWizardModal__TestsLead">
                The new sample will be retested for the following tests:
              </span>
              {tests.length > 10 ? (
                <div className="SendNewSampleWizardModal__PillsScroll">
                  <StyledScrollbar>
                    <div className="SendNewSampleWizardModal__Pills">
                      {tests.map((test) => (
                        <span key={test} className="SendNewSampleWizardModal__Pill">{test}</span>
                      ))}
                    </div>
                  </StyledScrollbar>
                </div>
              ) : (
                <div className="SendNewSampleWizardModal__Pills">
                  {tests.map((test) => (
                    <span key={test} className="SendNewSampleWizardModal__Pill">{test}</span>
                  ))}
                </div>
              )}
              {selectedDate && (
                <span className="SendNewSampleWizardModal__DateNote">
                  {`You will submit the new sample on ${selectedDate.format("MM/DD/YYYY")}.`}
                </span>
              )}
            </div>
          </>
        )}
      </div>

      <div
        className={[
          "SendNewSampleWizardModal__Footer",
          showNoCreditsCharged ? "SendNewSampleWizardModal__Footer--review" : "",
        ].filter(Boolean).join(" ")}
      >
        {showNoCreditsCharged && (
          <span className="SendNewSampleWizardModal__NoCredits">
            <RefundIcon className="SendNewSampleWizardModal__NoCreditsIcon" aria-hidden />
            <span className="SendNewSampleWizardModal__NoCreditsLabel">No credits charged</span>
          </span>
        )}
        <div className="SendNewSampleWizardModal__FooterActions">
          <Button type="default" className="SendNewSampleWizardModal__CancelBtn" onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
          {step === STEP_TIMELINE ? (
            <Button
              type="primary"
              className="SendNewSampleWizardModal__SubmitBtn"
              disabled={!selectedDate}
              onClick={handleContinue}
            >
              Continue
            </Button>
          ) : (
            <Button
              type="primary"
              className="SendNewSampleWizardModal__SubmitBtn"
              loading={loading}
              onClick={handleSubmit}
            >
              Submit
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}
